import {
  netClient,
  type CommonResponse,
  LIFE_SET_BASE_INFO,
  LIFE_LIST,
  MEMORIAL_ARTICLE,
  MEMORIAL_ARTICLE_CREATE,
  LIFE_DETAIL,
  MEMORIAL_ARTICLE_EDIT,
  LIFE_GET_BY_SERVICE_ID,
  LIFE_ARCHIVE,
  LIFE_RECOVER,
  ARTICLE_MESSAGES,
  LIFE_CAP,
  LIFE,
  LIFE_SET_CONFIG,
  ARTICLE_SET_TOP,
  OBITUARY_SET_TOP,
  OBITUARY_SET_HIDE,
  LIFE_SET_AVATAR,
  LIFE_CREATE_IN_ONE,
  LIFE_EDIT_IN_ONE,
  LIFE_SET_OPEN_TO_PUB
} from '@/apis/api'
import type { FileDTO } from '../media/media'
import { useUserLocalStorage, type Name, type UserMinDTO } from '../user/users'
import { defineStore } from 'pinia'
import { renderMessage } from '@/utils/util'
import { handleErr } from '../exception/handler'
import { computed, type Ref } from 'vue'
import { CODE_OK } from '@/apis/code'
import { ElMessage } from 'element-plus'
import type { LifeAnniversaryDTO } from '../schedule/schedule'
import type { LifeArticleDTO, LifeFileWithRelationDTO, MemorialMessageDTO, MemorialPostDTO } from '../memorize/memorize'
import { tryGetGifts } from '../memorize/gift'
import { useLocalStorage } from '@vueuse/core'

// 导入mock数据
import articleMockData from '@/mock/article'
import type { ObituaryDTO } from '../obituary/obituary'
// life in application
const KEY_LIFE = 'lives'
const lifeListLocal: Ref<LifeArrayData> = useUserLocalStorage<LifeArrayData>(KEY_LIFE, getDefaultListArrayData)

function getDefaultListArrayData() {
  return {
    lifeList: []
  }
}
export interface LifeArrayData {
  lifeList: Array<LifeDTO>
}

// ==== relation 关系 ======
export interface RelationDTO {
  userId: string
  lifeId: string
  name: string
  code: string
}

// ======== 创建life =========
export interface CreateLifeBody {
  avatarId: string
  fullName: string
  firstName: string
  middleName: string
  familyName: string
  gender: number
  relationCode: string
  relationName: string
  relation: RelationDTO
  force: boolean
}

export interface SetBaseInfoBody {
  lifeId: string
  birthday: string
  birthdayCalType: number
  birthPlace: Address
  dayDied: string
  dayDiedCalType: number
  placeDied: Address
  passAway: boolean
}

//推测的创建纪念服务body
export interface createMemorialServiceBody {
  avatarId: string
  fullName: string
  firstName: string
  middleName: string
  familyName: string
  gender: number
  relationCode: string
  force: boolean
  birthday: string
  birthdayCalType: number
  birthPlace: Address
  dayDied: string
  dayDiedCalType: number
  placeDied: Address
  passAway: boolean
}

export interface EditLifeCommand {
  lifeId: string
  fullName: string
  firstName: string
  middleName: string
  familyName: string
  gender: number
  avatarId: string
  relationCode: string
  relationName: string
}

export interface CreateLifeResponse {
  lifeId: string
}

export interface Address {
  country: string
  countryCode: string
  province: string
  provinceCode: string
  city: string
  cityCode: string
  county: string
  countyCode: string
  details: string
  fullAddress: string
}

export interface ComplexDate {
  date: number
  calType: number
}

export interface SetBaseInfoResponse {
  life: LifeDTO
  defaultSid: string
  dataChanged: boolean
}

export function setBaseInfo(setBaseInfoBody: SetBaseInfoBody) {
  return netClient.request<CommonResponse<SetBaseInfoResponse>>(LIFE_SET_BASE_INFO, 'post', setBaseInfoBody)
}

export interface SayToDTO {
  id: string
  lifeId: string
  userId: string
  createdAt: number
}

// export interface LifeDTO {
//   id: string
//   name: Name
//   gender: number
//   avatar: FileDTO
//   birthday: ComplexDate | null
//   birthPlace: Address | null
//   dayDied: ComplexDate | null
//   placeDied: Address | null
//   passAway: boolean
//   archived: boolean
//   serviceShare: boolean
//   sensitive: number
//   sensitiveReason: string
//   creatorRelation: RelationDTO
//   creator: UserMinDTO
//   createdAt: number
//   updatedAt: number
//   memorialServices: Array<MemorialServiceDTO>
//   sayTos: Array<SayToDTO>
//   altar: AltarDTO; // 增加一个祭拜的model，如果有就说明创建了小祭台
// }

export interface AltarDTO {
  id: string // 小祭台的id
  lifeId: string
  altarName: string
  creatorId: string
  avatarId: string
  avatar: FileDTO // 头像
  lastWorshipTime: number // 上次祭拜时间
  worshipDays: number // 总共的祭拜天数
  continuousWorshipDays: number // 连续祭拜的天数
  worshipStatus: number // 祭拜状态 0-未开始祭拜 1-正在祭拜 2-祭拜过但是贡品消耗完停止了
  settingShowed: boolean // 设置按钮的tips是否展示过
  avatarSettingShowed: boolean // 展示过头像设置的tip吗
  freeOfferingTimes: number // 给过免费的三柱香祭品吗？ > 0 就是给过
  worshipInMemorial: boolean // 是否在纪念馆展示，true - 展示  false - 不展示
  //bgm: MusicDTO; // 背景音乐
  createdAt: number
  updatedAt: number
}

export function getLifeList() {
  return netClient.request<CommonResponse<Array<LifeDTO>>>(LIFE_LIST, 'get', {})
}

export function getLifeListArchived() {
  return netClient.request<CommonResponse<Array<LifeDTO>>>(LIFE_LIST, 'get', { archived: true })
}

export const useLifeListStore = defineStore('lifeList', () => {
  const storage = useLocalStorage(
    'lifeListStore',
    {
      lifeList: [] as LifeDTO[],
      state: 'loading' as 'loading' | 'loaded' | 'failed'
    },
    {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : { lifeList: [], state: 'loading' }),
        write: (v) => JSON.stringify(v)
      }
    }
  )

  // 响应式状态
  const lifeList = computed({
    get: () => storage.value.lifeList,
    set: (val) => (storage.value.lifeList = val)
  })

  const state = computed({
    get: () => storage.value.state,
    set: (val) => (storage.value.state = val)
  })

  // 操作方法
  const updateLifeList = (newList: LifeDTO[]) => {
    lifeList.value = [...newList]
    state.value = 'loaded'
    console.log('pinia储存了life：', lifeList.value)
  }

  const replaceById = (life: LifeDTO) => {
    const index = lifeList.value.findIndex((item: LifeDTO) => life.id === item.id)
    if (index !== -1) {
      console.log('replaceById内部开始替换', index, life)
      // 如果找到了，更新现有项
      const newList = [...lifeList.value]
      newList[index] = life
      lifeList.value = newList
      console.log('replaceById内部结束替换', lifeList.value)
    } else {
      // 如果没找到，添加到列表末尾
      lifeList.value = [...lifeList.value, life]
    }
  }

  const loadFailed = () => {
    state.value = 'failed'
    lifeList.value = []
  }

  const loading = () => {
    state.value = 'loading'
  }

  // 计算属性
  const searchForItem = computed(
    () => (id?: string) => (id ? lifeList.value.find((item: LifeDTO) => item.id === id) : undefined)
  )

  const searchByServiceId = computed(
    () => (serviceId?: string) =>
      serviceId ? lifeList.value.find((item: LifeDTO) => item.memorialServices?.[0]?.id === serviceId) : undefined
  )

  const searchBySayToId = computed(
    () => (sayToId?: string) =>
      sayToId ? lifeList.value.find((item: LifeDTO) => item.sayTos?.[0]?.id === sayToId) : undefined
  )

  const getStoredLifeList = computed(() => lifeList.value)

  return {
    lifeList,
    state,
    updateLifeList,
    replaceById,
    loadFailed,
    loading,
    searchForItem,
    searchByServiceId,
    searchBySayToId,
    getStoredLifeList
  }
})

export async function loadLifeListData() {
  const lifeListStore = useLifeListStore()
  lifeListStore.loading()
  const { data, error, statusCode } = await getLifeList()
  if (error.value) {
    handleErr(data, error, statusCode)
    lifeListStore.loadFailed()
  } else {
    const resData = data as Ref<CommonResponse<Array<LifeDTO>>>
    if (resData.value.code === CODE_OK) {
      // success
      // console.log('get life list success', resData.value.data)
      const lifeListResponse: Array<LifeDTO> = resData.value.data
      lifeListStore.updateLifeList(lifeListResponse)

      setTimeout(() => {
        tryGetGifts()
      }, 3000)
    } else {
      lifeListStore.loadFailed()
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

// ====== life =======
export type PermissionValue = 'MANAGE' | 'WRITE' | 'READ' | 'NONE'

export interface Permission {
  name: string
  value: PermissionValue
}

export interface MemorialPrivilegeDTO {
  lifeId: string
  serviceId: string
  userId: string
  user: UserMinDTO
  role: string
  permissions: Array<Permission>
}

export interface LifeStatisticsDTO {
  lifeId: string
  serviceStat: Record<string, MemorialServiceStat>
  sayToStatMap: Record<string, SayToStat>
}

export interface MemorialServiceStat {
  msgCount: number
  mediaCount: number
  storyCount: number
}

export interface SayToStat {
  sayToCount: number
}

export interface GetLifeResponse {
  life: LifeDTO
  lifeStat: LifeStatisticsDTO
  recentAnniversaries: Array<LifeAnniversaryDTO>
  lifeConfig: LifeConfigDTO
}

export interface IsMemberDTO {
  userId: string
  member: boolean
}

export interface MemorialServiceDTO {
  id: string
  lifeId: string
  type: number
  name: string
  creatorId: string
  isCreatorMember: IsMemberDTO
  creatorPrivilege: MemorialPrivilegeDTO
  createdAt: number
}

export function getLifeDetails(id: string) {
  return netClient.request<CommonResponse<GetLifeResponse>>(LIFE_DETAIL + '/' + id, 'get', {})
}

export function getLife(id: string) {
  return netClient.request<CommonResponse<LifeDTO>>(LIFE + '/' + id, 'get', {})
}

export function getLifeByServiceId(serviceId: string) {
  return netClient.request<CommonResponse<GetLifeResponse>>(LIFE_GET_BY_SERVICE_ID, 'get', { serviceId: serviceId })
}

export interface SectionAlbum {
  medias: Array<LifeFileWithRelationDTO>
  total: number
}

export interface MemorialArticleDTO {
  id: string
  lifeId: string
  title: string
  baseInfo: string
  content: string
  charNumber: number
  serviceId: string
  creator: UserMinDTO
  updater: UserMinDTO
  createdAt: number
  updatedAt: number
  top: boolean // 置顶
  hide: boolean // 隐藏
  publishedArticleId: string // 已发布文章id
}

export interface ArticleWrapper {
  article: boolean
  memorialArticleDTO: MemorialArticleDTO
  obituaryDTO: ObituaryDTO
  createdAt: number
  top: boolean
  hide: boolean
}

export interface ArticleAndMessageResponse {
  articles: Array<ArticleWrapper> // 排序了的文章和讣告混合体
  sectionAlbum: SectionAlbum
  sectionLifeArticle: LifeArticleDTO
  sectionMemorialPost: MemorialPostDTO
  id: string
  pageSize: number
  total: number
  items: Array<MemorialMessageDTO>
}

export function getMemorialArticleByServiceId(serviceId: string) {
  return netClient.request<CommonResponse<Array<ArticleWrapper>>>(MEMORIAL_ARTICLE, 'get', { serviceId: serviceId })
}

export function getArticleAndMessage(serviceId: string, last: string | null, pageSize: number) {
  return netClient.request<CommonResponse<ArticleAndMessageResponse>>(
    ARTICLE_MESSAGES,
    'get',
    last
      ? {
          serviceId: serviceId,
          last: last,
          pageSize: pageSize
        }
      : {
          serviceId: serviceId,
          pageSize: pageSize
        }
  )
}

export function setArticleTop(articleId: string, top: boolean) {
  return netClient.request<CommonResponse<any>>(ARTICLE_SET_TOP, 'post', { articleId: articleId, top: top })
}

export function setObituaryTop(obituaryId: string, top: boolean) {
  return netClient.request<CommonResponse<any>>(OBITUARY_SET_TOP, 'post', { obituaryId: obituaryId, top: top })
}

export interface SetObituaryHideCommand {
  obituaryId: string
  hide: boolean
}
export function setObituaryHide(hideCommand: SetObituaryHideCommand) {
  return netClient.request<CommonResponse<any>>(OBITUARY_SET_HIDE, 'post', hideCommand)
}

export interface CreateMemorialArticleBody {
  title: string
  baseInfo: string
  content: string
  serviceId: string
}

export interface CreateMemorialArticleResponse {
  articleId: string
}

export interface DeleteMemorialArticleResponse {
  articleId: string
}

export function getMemorialArticle(id: string) {
  return netClient.request<CommonResponse<MemorialArticleDTO>>(MEMORIAL_ARTICLE + '/' + id, 'get', {})
}

export function createMemorialArticle(createArticle: CreateMemorialArticleBody) {
  return netClient.request<CommonResponse<CreateMemorialArticleResponse>>(
    MEMORIAL_ARTICLE_CREATE,
    'post',
    createArticle
  )
}

export interface EditMemorialAritcleBody {
  articleId: string
  title: string
  baseInfo: string
  content: string
  serviceId: string
}

export function editMemorialArticle(editBody: EditMemorialAritcleBody) {
  return netClient.request<CommonResponse<CreateMemorialArticleResponse>>(MEMORIAL_ARTICLE_EDIT, 'post', editBody)
}

export function deleteMemorialArticle(articleId: string) {
  return netClient.request<CommonResponse<DeleteMemorialArticleResponse>>(
    MEMORIAL_ARTICLE + '/' + articleId,
    'delete',
    {}
  )
}

export interface ArchiveLifeCommand {
  lifeId: string
}

export interface RecoverLifeCommand {
  lifeId: string
}

export function archiveLife(archiveCommand: ArchiveLifeCommand) {
  return netClient.request<CommonResponse<any>>(LIFE_ARCHIVE, 'post', archiveCommand)
}

export function recoverLife(recoverCommand: RecoverLifeCommand) {
  return netClient.request<CommonResponse<any>>(LIFE_RECOVER, 'post', recoverCommand)
}

export function finalDeleteLife(lifeId: string) {
  return netClient.request<CommonResponse<any>>(LIFE + '/' + lifeId, 'delete', {})
}

export function checkLifeCap() {
  return netClient.request<CommonResponse<any>>(LIFE_CAP, 'get', {})
}

export interface LifeConfigDTO {
  id: string
  lifeId: string
  smsEnabled: boolean
}

export interface LifeConfigCommand {
  lifeId: string
  smsEnabled: boolean
}

export function updateLifeConfig(configCommand: LifeConfigCommand) {
  return netClient.request<CommonResponse<LifeConfigDTO>>(LIFE_SET_CONFIG, 'post', configCommand)
}

export function changeLifeAvatar(lifeId: string, avatarId: string) {
  return netClient.request<CommonResponse<LifeDTO>>(LIFE_SET_AVATAR, 'post', { lifeId: lifeId, avatarId: avatarId })
}

export interface LifeDTO {
  id: string
  name: Name
  gender: number
  avatar: FileDTO
  birthday: ComplexDate
  birthPlace: Address
  dayDied: ComplexDate
  placeDied: Address
  passAway: boolean
  archived: boolean
  serviceShare: boolean
  sensitive: number
  sensitiveReason: string
  altar: AltarDTO
  creatorRelation: RelationDTO
  creator: UserMinDTO
  createdAt: number
  updatedAt: number
  memorialServices: Array<MemorialServiceDTO>
  sayTos: Array<SayToDTO>
  openToPub: boolean // 开放
  publicFigure: boolean // 是否公众人物
  figureCategory: string // 公众人物分类
  figureSummary: string // 公众人物简介
  figureTop: boolean // 是否置顶公众人物 - 后端排序字段，前端忽略
  favorite: boolean // 被精选
  firstArticle: ArticleMinDTO
}
export interface ArticleMinDTO {
  id: string
  title: string
  lifeId: string
  content: string
}

export interface CreateInOneCommand {
  fullName: string
  firstName: string
  middleName: string
  familyName: string
  gender: number
  avatarId: string
  relationCode: string
  relationName: string
  whaticall: string // 我对亲人的称呼
  birthday: string
  dayDied: string
  birthdayCalType: number
  dayDiedCalType: number
  birthPlace: Address
  isPassAway: boolean
  placeDied: Address
  force: boolean
  openToPub: boolean // 开放
}

export function createLifeInOne(createLife: CreateInOneCommand) {
  return netClient.request<CommonResponse<LifeDTO>>(LIFE_CREATE_IN_ONE, 'post', createLife)
}

export interface EditInOneCommand {
  lifeId: string
  fullName: string
  firstName: string
  middleName: string
  familyName: string
  gender: number
  avatarId: string
  relationCode: string
  relationName: string
  birthday: string
  dayDied: string
  whaticall: string
  birthdayCalType: number
  dayDiedCalType: number
  birthPlace: Address
  isPassAway: boolean
  placeDied: Address
}

export function editLifeInOne(body: EditInOneCommand) {
  return netClient.request<CommonResponse<LifeDTO>>(LIFE_EDIT_IN_ONE, 'post', body)
}

export interface SetLifeOpenCommand {
  lifeId: string
  openToPub: boolean
}

export function setLifeOpenToPub(body: SetLifeOpenCommand) {
  return netClient.request<CommonResponse<LifeDTO>>(LIFE_SET_OPEN_TO_PUB, 'post', body)
}
